import { FormState } from "final-form";
import { ReactNode } from "react";
import { useForm, useFormState } from "react-final-form";

import {
  Config,
  dataAttribute,
  DataAttributes,
  getAsteriskStyles,
  getConfig,
  mergeStyleSets,
  metaToLabelColor,
  Text,
  TextProps,
  useTheme,
  Variant
} from "@bps/fluent-ui";
import { useSubmissionFormMetaContext } from "@ui-components/form/submission-form/context/SubmissionFormMetaContext.tsx";

export const hasChildErrors = (
  childNames: string[],
  {
    errors,
    submitFailed,
    touched,
    modified
  }: Pick<
    FormState<any, any>,
    "errors" | "submitFailed" | "touched" | "modified"
  >
): boolean => {
  return (
    !!errors &&
    Object.keys(errors).some(err => {
      //Handle errors for array fields with mutliple children
      if (Array.isArray(errors[err])) {
        return errors[err].find((error: string, index: any) => {
          return childNames.some(
            name =>
              name.includes(`${err}[${index}]`) &&
              (submitFailed || (touched?.[name] && modified?.[name]))
          );
        });
      } else {
        return childNames.some(
          name =>
            name.includes(err) &&
            (submitFailed || (touched?.[name] && modified?.[name]))
        );
      }
    })
  );
};

interface FieldsetLegendProps {
  legend: ReactNode;
  legendLevel?: Variant;
  hasAsterisk?: boolean;
  inputProps: { name: string; disabled?: boolean }[];
  styles?: TextProps["styles"];
}

export const FieldsetLegend = (props: FieldsetLegendProps) => {
  const { legend, legendLevel, inputProps, hasAsterisk } = props;
  const { getFieldState } = useForm();

  const theme = useTheme();
  const { disabled: formIsDisabled } = useSubmissionFormMetaContext();
  const { active, errors, modified, submitFailed, touched } = useFormState({
    subscription: {
      active: true,
      errors: true,
      modified: true,
      submitFailed: true,
      touched: true
    }
  });

  const registeredInputs = inputProps.filter(
    ({ name }) => !!getFieldState(name)
  );

  const disabled =
    formIsDisabled ||
    (registeredInputs.length > 0 &&
      registeredInputs.every(inputProp => inputProp.disabled));

  const { variant, fontWeight }: Config = getConfig(legendLevel);

  // check if the current active fields is a part of the group
  const isActive: boolean =
    !!active && inputProps.some(input => input.name === active.toString());

  // check if there are validation errors among the children fields

  const hasErrorMessage: boolean = hasChildErrors(
    inputProps.map(input => input.name),
    {
      errors,
      submitFailed,
      touched,
      modified
    }
  );

  return (
    <Text
      as="legend"
      {...dataAttribute(DataAttributes.Element, "fieldset-legend")}
      data-active={isActive}
      variant={variant}
      block
      styles={mergeStyleSets(props.styles, {
        root: {
          fontWeight,
          color: disabled
            ? theme.semanticColors.disabledText
            : metaToLabelColor({ active: isActive, hasErrorMessage }, theme),
          padding: 0,
          position: "relative",
          selectors: hasAsterisk ? getAsteriskStyles(theme) : undefined
        }
      })}
    >
      {legend}
    </Text>
  );
};
